import React from 'react';
import PropTypes from 'prop-types';
import ActivityItem from './ActivityItem';
import { splitByStatus, isSameDay } from './activityUtils';

const SectionHeading = ({ label, count }) => (
  <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-slate-50/80 tw-border-y tw-border-slate-100">
    <span className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400">{label}</span>
    <span className="tw-text-[11px] tw-font-semibold tw-text-slate-400">{count}</span>
    <span aria-hidden className="tw-flex-1 tw-h-px tw-bg-slate-200/70" />
  </div>
);

SectionHeading.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
};

const Section = ({ items, onEdit, projectId, actions }) => (
  <>
    {items.map((item, idx) => (
      <ActivityItem
        key={`${item._type}-${item.id}`}
        item={item}
        onEdit={onEdit}
        projectId={projectId}
        isFirst={idx === 0}
        isLast={idx === items.length - 1}
        // The date gutter repeats only when the day changes, so a busy day reads
        // as one block instead of the same date stamped on every row.
        showDate={idx === 0 || !isSameDay(item, items[idx - 1])}
        {...actions}
      />
    ))}
  </>
);

Section.propTypes = {
  items:     PropTypes.array.isRequired,
  onEdit:    PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  actions:   PropTypes.object,
};

Section.defaultProps = { projectId: undefined, actions: undefined };

/**
 * The activity feed rows: open items newest-first on top, completed/cancelled
 * ones below an "Activity history" heading. The caller owns the surrounding
 * card so the filter header can stay visible when the feed is empty.
 */
const ActivityTimeline = ({ items, onEdit, projectId, loading, actions }) => {
  const { open, history } = splitByStatus(items);

  return (
    <div className={`tw-transition-opacity ${loading ? 'tw-opacity-60' : ''}`}>
      {open.length > 0 && <Section items={open} onEdit={onEdit} projectId={projectId} actions={actions} />}

      {history.length > 0 && (
        <>
          <SectionHeading label="Activity history" count={history.length} />
          <div className="tw-opacity-80 hover:tw-opacity-100 tw-transition-opacity">
            <Section items={history} onEdit={onEdit} projectId={projectId} actions={actions} />
          </div>
        </>
      )}
    </div>
  );
};

ActivityTimeline.propTypes = {
  items:     PropTypes.array.isRequired,
  onEdit:    PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading:   PropTypes.bool,
  /** Optional slice-action overrides forwarded to each row. */
  actions:   PropTypes.object,
};

ActivityTimeline.defaultProps = { projectId: undefined, loading: false, actions: undefined };

export default ActivityTimeline;
